import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBandeau } from '../BandeauContext';
import { apiClient } from '../../utils/apiClient';
import { generateSlug } from '../../utils/slugUtils';
import { exerciseThemeSlug } from '../student/SkillsView';
import MarkdownEditor from './MarkdownEditor';
import TabBar from '../TabBar';
import ActionBtn from './ActionBtn';

const BASE = process.env.REACT_APP_API_BASE_URL;

const EMPTY_SKILL = {
    id: null, subject_id: '', title: '',
    model_question: '', model_explanation: '', model_correction: '',
    notion_ids: [],
    criteria: [], exercises: [],
};

const EMPTY_EXERCISE = {
    id: null, theme: '', question: '', correction: '', sort_order: 0,
    excluded_criterion_ids: [], specific_criteria: [],
};
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</label>
            {children}
        </div>
    );
}

function Input({ value, onChange, placeholder, type = 'text' }) {
    return (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
    );
}

// ── Multi-select notions ─────────────────────────────────────────────────────
function NotionMultiSelect({ notions, selectedIds, onChange, filterSubjectId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = filterSubjectId
        ? notions.filter(n => String(n.subject_id) === String(filterSubjectId))
        : notions;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedNotions = notions.filter(n => selectedIds.includes(n.id));

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen(o => !o)}
                className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-left flex flex-wrap gap-1 min-h-[34px]">
                {selectedNotions.length === 0
                    ? <span className="text-gray-400">— Aucune notion sélectionnée —</span>
                    : selectedNotions.map(n => (
                        <span key={n.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300 text-xs font-medium">
                            {n.number ? `${n.number} – ` : ''}{n.name}
                            <span onClick={e => { e.stopPropagation(); toggle(n.id); }} className="hover:text-red-500 cursor-pointer">✕</span>
                        </span>
                    ))
                }
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-xl">
                    {filtered.length === 0 && <div className="px-3 py-2 text-sm text-gray-400 italic">Aucune notion pour cette matière.</div>}
                    {filtered.map(n => (
                        <label key={n.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                            <input type="checkbox" checked={selectedIds.includes(n.id)} onChange={() => toggle(n.id)}
                                className="rounded border-gray-300 text-cyan-500 focus:ring-cyan-500" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {n.number ? <span className="text-gray-400 mr-1">{n.number}</span> : null}
                                {n.name}
                                {n.subject_name && !filterSubjectId && <span className="text-xs text-gray-400 ml-1">({n.subject_name})</span>}
                            </span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminSkills() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setBandeau, resetBandeau } = useBandeau();
    const [skills, setSkills]     = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [notions, setNotions]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [selected, setSelected]       = useState(null);
    const [busy, setBusy]               = useState(false);
    const [search, setSearch]           = useState('');
    const [filterSubject, setFilterSubject] = useState('');
    const [editTab, setEditTab]         = useState('infos');
    const [selectedExIdx, setSelectedExIdx] = useState(null);

    const fromUrl      = location.state?.fromUrl      ?? null;
    const skillTitle   = location.state?.skillTitle   ?? null;
    const subjectName  = location.state?.subjectName  ?? null;
    const chapterName  = location.state?.chapterName  ?? null;
    const subjectUrl   = location.state?.subjectUrl   ?? null;
    const chapterUrl   = location.state?.chapterUrl   ?? null;
    const exercicesUrl = location.state?.exercicesUrl ?? null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/admin_skills.php?action=list`);
            const json = await res.json();
            if (json.success) {
                setSkills(json.skills);
                setSubjects(json.subjects);
                setNotions(json.notions ?? []);
            }
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openSkill = useCallback((id) => {
        const sk = skills.find(s => s.id === id);
        if (sk) { setSelected({ ...sk, notion_ids: sk.notion_ids ?? [] }); setEditTab('infos'); setSelectedExIdx(null); }
    }, [skills]);

    // Auto-select skill when navigated from CourseView with a skillTitle
    useEffect(() => {
        if (!skillTitle || skills.length === 0 || selected) return;
        const match = skills.find(sk => sk.title === skillTitle);
        if (match) openSkill(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skillTitle, skills]);

    // Auto-open new skill form when navigated with openNew flag
    useEffect(() => {
        if (!location.state?.openNew || selected || loading) return;
        setSelected({ ...EMPTY_SKILL });
        setEditTab('infos');
        setSelectedExIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.openNew, loading]);

    // Bandeau
    useEffect(() => {
        if (selected) {
            const crumbs = subjectName ? [
                { label: 'Apprendre',   onClick: () => navigate(subjectUrl  ?? '/apprendre') },
                { label: subjectName,   onClick: () => navigate(subjectUrl  ?? '/apprendre') },
                { label: chapterName,   onClick: () => navigate(chapterUrl  ?? fromUrl ?? '/apprendre') },
                { label: 'Exercices',   onClick: () => navigate(exercicesUrl ?? fromUrl ?? '/apprendre') },
            ] : [
                { label: 'Exercices',   onClick: () => setSelected(null) },
            ];
            setBandeau({ title: selected.title || 'Nouvel exercice', breadcrumb: crumbs });
        } else {
            setBandeau({ title: 'Exercices', breadcrumb: null });
        }
        return () => resetBandeau();
    }, [selected?.id, selected?.title, setBandeau, resetBandeau]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveSkill = async () => {
        if (!selected?.title?.trim()) return alert('Le titre est requis.');
        setBusy(true);
        const res  = await apiClient(`${BASE}/admin_skills.php?action=save_skill`, {
            method: 'POST', body: JSON.stringify(selected),
        });
        const json = await res.json();
        setBusy(false);
        if (json.success) { await load(); setSelected(null); }
        else alert(json.message);
    };

    const deleteSkill = async (sk) => {
        if (!window.confirm(`Supprimer "${sk.title}" ? Cette action est irréversible.`)) return;
        setBusy(true);
        await apiClient(`${BASE}/admin_skills.php?action=delete_skill`, {
            method: 'POST', body: JSON.stringify({ id: sk.id }),
        });
        setSelected(null);
        await load();
        setBusy(false);
    };

    const filtered = skills.filter(sk => {
        const q = search.toLowerCase();
        const matchSearch = !q || sk.title.toLowerCase().includes(q);
        const matchSubj   = !filterSubject || String(sk.subject_id) === filterSubject;
        return matchSearch && matchSubj;
    });

    // ── Critères globaux ──────────────────────────────────────────
    const setCriteria = (criteria) => setSelected(s => ({ ...s, criteria }));
    const addCriterion = () => setCriteria([...(selected.criteria ?? []), { id: null, label: '' }]);
    const updateCriterion = (i, val) => {
        const next = [...selected.criteria];
        next[i] = { ...next[i], label: val };
        setCriteria(next);
    };
    const removeCriterion = (i) => setCriteria(selected.criteria.filter((_, idx) => idx !== i));
    const moveCriterion = (i, dir) => {
        const next = [...selected.criteria];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setCriteria(next);
    };

    // ── Exercices ────────────────────────────────────────────────
    const setExercises = (exercises) => setSelected(s => ({ ...s, exercises }));
    const addExercise  = () => setExercises([...(selected.exercises ?? []), { ...EMPTY_EXERCISE }]);
    const updateExercise = (i, k, v) => {
        const next = [...selected.exercises];
        next[i] = { ...next[i], [k]: v };
        setExercises(next);
    };
    const removeExercise = (i) => setExercises(selected.exercises.filter((_, idx) => idx !== i));
    const moveExercise = (i, dir) => {
        const next = [...selected.exercises];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setExercises(next);
    };

    // Excluded criteria toggle for an exercise
    const toggleExcludedCriterion = (exIdx, critId) => {
        const ex = selected.exercises[exIdx];
        const excluded = ex.excluded_criterion_ids ?? [];
        const next = excluded.includes(critId)
            ? excluded.filter(x => x !== critId)
            : [...excluded, critId];
        updateExercise(exIdx, 'excluded_criterion_ids', next);
    };

    // Specific criteria for an exercise
    const addSpecificCriterion = (exIdx) => {
        const ex = selected.exercises[exIdx];
        updateExercise(exIdx, 'specific_criteria', [...(ex.specific_criteria ?? []), { id: null, label: '' }]);
    };
    const updateSpecificCriterion = (exIdx, cIdx, val) => {
        const ex = selected.exercises[exIdx];
        const next = [...(ex.specific_criteria ?? [])];
        next[cIdx] = { ...next[cIdx], label: val };
        updateExercise(exIdx, 'specific_criteria', next);
    };
    const removeSpecificCriterion = (exIdx, cIdx) => {
        const ex = selected.exercises[exIdx];
        updateExercise(exIdx, 'specific_criteria', (ex.specific_criteria ?? []).filter((_, idx) => idx !== cIdx));
    };

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    // ── Mode édition ─────────────────────────────────────────────
    if (selected) {
        const globalCriteria = selected.criteria ?? [];
        const exercises      = selected.exercises ?? [];

        const TABS = [
            { id: 'infos',     label: 'Informations' },
            { id: 'criteres',  label: `Critères (${globalCriteria.length})` },
            { id: 'exercice', label: `Exercices (${exercises.length})` },
        ];

        return (
            <div className="flex flex-col gap-4 h-full min-h-0">
                {/* ── TabBar + actions ── */}
                <div className="flex items-center justify-between gap-3 shrink-0">
                    <TabBar tabs={TABS} active={editTab} onChange={setEditTab} />
                    <div className="flex gap-2 shrink-0">
                        {selected.id && exercicesUrl && (
                            <ActionBtn variant="view"
                                icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                                onClick={() => navigate(`${exercicesUrl}/${generateSlug(selected.title)}`)}>Voir</ActionBtn>
                        )}
                        {selected.id && (
                            <ActionBtn variant="danger"
                                icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>}
                                onClick={() => deleteSkill(selected)}>Supprimer</ActionBtn>
                        )}
                        <ActionBtn variant="primary"
                            icon={busy ? null : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            onClick={saveSkill} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</ActionBtn>
                    </div>
                </div>

                {/* ── Informations ── */}
                {editTab === 'infos' && (
                    <div className="flex-1 overflow-y-auto min-h-0 space-y-5 pr-1">
                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6 space-y-4">
                            <Field label="Titre">
                                <Input value={selected.title} onChange={v => setSelected(s => ({ ...s, title: v }))} placeholder="ex: Résoudre une équation du premier degré" />
                            </Field>
                            <Field label="Matière">
                                <select value={selected.subject_id ?? ''} onChange={e => setSelected(s => ({ ...s, subject_id: e.target.value || null, notion_ids: [] }))}
                                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500">
                                    <option value="">— Sans matière —</option>
                                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </Field>
                            <Field label="Notions prérequises">
                                <NotionMultiSelect
                                    notions={notions}
                                    selectedIds={selected.notion_ids ?? []}
                                    onChange={ids => setSelected(s => ({ ...s, notion_ids: ids }))}
                                    filterSubjectId={selected.subject_id}
                                />
                            </Field>
                        </div>
                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6 space-y-4">
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Exercice modèle</p>
                            <MarkdownEditor label="Question modèle" value={selected.model_question} onChange={v => setSelected(s => ({ ...s, model_question: v }))} rows={4} placeholder="La question type posée en évaluation…" />
                            <MarkdownEditor label="Explication de la méthode" value={selected.model_explanation} onChange={v => setSelected(s => ({ ...s, model_explanation: v }))} rows={6} placeholder="Détail de la méthode pas-à-pas avec LaTeX ($x^2$)…" />
                            <MarkdownEditor label="Correction modèle" value={selected.model_correction} onChange={v => setSelected(s => ({ ...s, model_correction: v }))} rows={4} placeholder="La correction concise…" />
                        </div>
                    </div>
                )}

                {/* ── Critères ── */}
                {editTab === 'criteres' && (
                    <div className="flex-1 overflow-y-auto min-h-0 pr-1">
                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6 space-y-3">
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-white">Critères de réussite</p>
                                    <p className="text-xs text-gray-400 mt-0.5">S'appliquent à tous les exercices par défaut. Peuvent être exclus ou complétés déclinaison par déclinaison.</p>
                                </div>
                                <ActionBtn variant="add" onClick={addCriterion}>+ Ajouter</ActionBtn>
                            </div>
                            {globalCriteria.length === 0 && <p className="text-sm text-gray-400 italic text-center py-8">Aucun critère.</p>}
                            {globalCriteria.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 bg-white dark:bg-white/[0.03] rounded-xl border border-gray-200 dark:border-white/[0.05] px-3 py-2">
                                    <span className="text-xs font-mono text-gray-400 w-6 shrink-0">{i + 1}.</span>
                                    <input value={c.label} onChange={e => updateCriterion(i, e.target.value)}
                                        className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 focus:outline-none"
                                        placeholder="ex: Identifier les termes semblables" />
                                    <button onClick={() => moveCriterion(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1">↑</button>
                                    <button onClick={() => moveCriterion(i, 1)} disabled={i === globalCriteria.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1">↓</button>
                                    <button onClick={() => removeCriterion(i)} className="text-red-400 hover:text-red-600 px-1 text-sm">✕</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Exercices ── */}
                {editTab === 'exercice' && (
                    <div className="flex-1 min-h-0 flex gap-4">
                        {/* Liste */}
                        <div className="w-64 shrink-0 flex flex-col gap-2 overflow-y-auto pr-1">
                            <ActionBtn variant="add" className="w-full justify-center" onClick={() => { addExercise(); setSelectedExIdx(exercises.length); }}>+ Ajouter</ActionBtn>
                            {exercises.length === 0 && (
                                <p className="text-sm text-gray-400 italic text-center py-8">Aucune déclinaison.</p>
                            )}
                            {exercises.map((ex, i) => (
                                <button key={i} onClick={() => setSelectedExIdx(i)}
                                    className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                                        selectedExIdx === i
                                            ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-700 dark:text-cyan-300'
                                            : 'bg-white/70 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.05] hover:border-cyan-300 dark:hover:border-cyan-600 text-gray-700 dark:text-gray-300'
                                    }`}>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">Déclinaison {i + 1}</p>
                                    {ex.theme && <p className="text-sm font-semibold truncate">{ex.theme}</p>}
                                    <p className={`text-xs truncate mt-0.5 ${selectedExIdx === i ? 'text-cyan-600/70 dark:text-cyan-400/70' : 'text-gray-400'}`}>
                                        {ex.question ? ex.question.slice(0, 60) + (ex.question.length > 60 ? '…' : '') : <em>Sans énoncé</em>}
                                    </p>
                                </button>
                            ))}
                        </div>

                        {/* Éditeur de la déclinaison sélectionnée */}
                        <div className="flex-1 min-w-0 overflow-y-auto">
                            {selectedExIdx === null || !exercises[selectedExIdx] ? (
                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                    Sélectionne une déclinaison pour la modifier
                                </div>
                            ) : (() => {
                                const i = selectedExIdx;
                                const ex = exercises[i];
                                const excluded = ex.excluded_criterion_ids ?? [];
                                const specific = ex.specific_criteria ?? [];
                                return (
                                    <div className="space-y-4">
                                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-bold text-gray-700 dark:text-gray-200">Déclinaison {i + 1}</p>
                                                <div className="flex gap-2">
                                                    <button onClick={() => moveExercise(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-sm px-1">↑</button>
                                                    <button onClick={() => moveExercise(i, 1)} disabled={i === exercises.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-sm px-1">↓</button>
                                                    {exercicesUrl && ex.id && (
                                                        <ActionBtn variant="view"
                                                            icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                                                            onClick={() => navigate(`${exercicesUrl}/${generateSlug(selected.title)}/${exerciseThemeSlug(ex, i)}`)}>Voir</ActionBtn>
                                                    )}
                                                    <ActionBtn variant="danger"
                                                        icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>}
                                                        onClick={() => { removeExercise(i); setSelectedExIdx(null); }}>Supprimer</ActionBtn>
                                                </div>
                                            </div>
                                            <Field label="Thème / Contexte">
                                                <Input value={ex.theme} onChange={v => updateExercise(i, 'theme', v)} placeholder="ex: Laser Game" />
                                            </Field>
                                            <MarkdownEditor label="Énoncé" value={ex.question} onChange={v => updateExercise(i, 'question', v)} rows={4} placeholder="L'énoncé de l'exercice…" />
                                            <MarkdownEditor label="Correction" value={ex.correction} onChange={v => updateExercise(i, 'correction', v)} rows={4} placeholder="La correction détaillée…" />
                                        </div>

                                        {/* Critères pour cette déclinaison */}
                                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6 space-y-3">
                                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Critères pour cette déclinaison</p>

                                            {globalCriteria.length > 0 && (
                                                <div className="space-y-1">
                                                    <p className="text-xs text-gray-500 mb-2">Critères globaux — décocher pour exclure :</p>
                                                    {globalCriteria.map((c, ci) => {
                                                        const isExcluded = c.id && excluded.includes(c.id);
                                                        return (
                                                            <label key={ci} className={`flex items-start gap-2 cursor-pointer rounded-lg px-3 py-2 transition-colors ${isExcluded ? 'opacity-40' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'}`}>
                                                                <input type="checkbox" checked={!isExcluded} disabled={!c.id}
                                                                    onChange={() => c.id && toggleExcludedCriterion(i, c.id)}
                                                                    className="mt-0.5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500" />
                                                                <span className={`text-sm ${isExcluded ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>{c.label || <em>Sans texte</em>}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className="pt-2 space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs text-gray-500">Critères spécifiques :</p>
                                                    <ActionBtn variant="add" onClick={() => addSpecificCriterion(i)}>+ Ajouter</ActionBtn>
                                                </div>
                                                {specific.length === 0 && <p className="text-xs text-gray-400 italic">Aucun.</p>}
                                                {specific.map((sc, sci) => (
                                                    <div key={sci} className="flex items-center gap-2">
                                                        <input value={sc.label} onChange={e => updateSpecificCriterion(i, sci, e.target.value)}
                                                            className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                            placeholder="Critère spécifique…" />
                                                        <button onClick={() => removeSpecificCriterion(i, sci)} className="text-red-400 hover:text-red-600 text-sm px-1">✕</button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Mode liste ───────────────────────────────────────────────
    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 w-48" />
                <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-500">
                    <option value="">Toutes les matières</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ActionBtn variant="add" onClick={() => { setSelected({ ...EMPTY_SKILL, criteria: [], exercises: [], notion_ids: [] }); setEditTab('infos'); setSelectedExIdx(null); }}>+ Nouvel exercice</ActionBtn>
                <span className="ml-auto text-xs text-gray-400">{filtered.length} exercice{filtered.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="space-y-2">
                {filtered.map(sk => (
                    <div key={sk.id} onClick={() => { setSelected({ ...sk, notion_ids: sk.notion_ids ?? [] }); setEditTab('infos'); setSelectedExIdx(null); }}
                        className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 hover:border-cyan-300 dark:hover:border-cyan-700 cursor-pointer transition-colors">
                        <div>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{sk.title}</span>
                            <div className="text-xs text-gray-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                <span>{sk.subject_name ?? 'Sans matière'}</span>
                                <span>{sk.criteria?.length ?? 0} critère{(sk.criteria?.length ?? 0) !== 1 ? 's' : ''}</span>
                                <span>{sk.exercises?.length ?? 0} déclinaison{(sk.exercises?.length ?? 0) !== 1 ? 's' : ''}</span>
                                {(sk.notion_ids?.length ?? 0) > 0 && (
                                    <span className="text-cyan-500">{sk.notion_ids.length} notion{sk.notion_ids.length !== 1 ? 's' : ''} prérequise{sk.notion_ids.length !== 1 ? 's' : ''}</span>
                                )}
                            </div>
                        </div>
                        <span className="text-gray-400 text-xs">→</span>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="text-center py-12 text-gray-400 text-sm">Aucun exercice trouvé.</div>
                )}
            </div>
        </div>
    );
}

